// imports
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';

// local imports
import { RootState, uiActions } from '../../store';
import { OutlinedLink } from './OutlinedLink';
import CustomDialog from '../CustomDialog';

// Styled components
const ContentWrapper = styled(Box)({
  minWidth: 450,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 24,
  padding: '16px 0',
});

const ButtonSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

const SectionLabel = styled(Typography)({
  fontWeight: 'bold',
  marginBottom: 4,
  textAlign: 'center',
});

/**
 * Data Download Dialog
 *
 * Renders a modal with data download options.
 *
 * @returns {JSX.Element} The rendered Data Download Dialog component.
 */
export default function DataDownloadDialog() {
  const { dataDownloadDialogOpen } = useSelector(
    (state: RootState) => state.ui
  );
  const { selectedSector, selectedModel, selectedVariable } = useSelector(
    (state: RootState) => state.waterData
  );
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(uiActions.toggleDataDownloadDialog());
  };

  // Base URL has different values depending on if we are running this app locally or in production
  // Hard-code url for local testing; use the relative path defined as a variable in the GitLab repo for prod
  const BASE_URL = import.meta.env.DEV
    ? `${import.meta.env.VITE_NWDC_S3_URL}/`
    : import.meta.env.VITE_CLOUDFRONT_PROXY_PREFIX;

  // Construct URLs for the links
  const subsetDownloadUrl = `${BASE_URL}subset-download?model=${selectedModel}&variable=${selectedVariable}`;
  const dataFileDirectoryUrl = `${BASE_URL}data-file-directory?path=data/${selectedSector}/${selectedModel}/${selectedVariable}/`;

  return (
    <CustomDialog
      open={dataDownloadDialogOpen}
      onClose={handleClose}
      title="Download Data"
      dataTestId="data-download-dialog"
    >
      <ContentWrapper>
        <ButtonSection>
          <SectionLabel variant="body1">
            Download a specific county or HUC12:
          </SectionLabel>
          <OutlinedLink
            href={subsetDownloadUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Subset & Download Tool
          </OutlinedLink>
        </ButtonSection>

        <ButtonSection>
          <SectionLabel variant="body1">Download all of CONUS:</SectionLabel>
          <OutlinedLink
            href={dataFileDirectoryUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Data File Directory
          </OutlinedLink>
        </ButtonSection>
      </ContentWrapper>
    </CustomDialog>
  );
}
